"use client";

import { useState } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Textarea, Select, SelectItem } from "@nextui-org/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import * as z from "zod";
import axios from "axios";
import { toast } from "react-hot-toast";

const formSchema = z.object({
  name: z.string().min(1, "Protocol name is required"),
  frequency: z.string().min(1, "Frequency is required"),
  description: z.string().min(1, "Description is required"),
});

interface CreateProtocolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateProtocolModal: React.FC<CreateProtocolModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      frequency: "",
      description: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);

      // Validate form data
      if (!values.name || !values.frequency || !values.description) {
        toast.error("Please fill in all required fields");
        return;
      }

      const response = await axios.post("/api/protocols", values);
      console.log("[PROTOCOL_CREATED]", response.data);
      toast.success("Protocol created successfully!");
      form.reset();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('[PROTOCOL_CREATE_ERROR]', {
        error,
        response: axios.isAxiosError(error) ? error.response?.data : undefined,
        status: axios.isAxiosError(error) ? error.response?.status : undefined
      });
      
      let errorMessage = "Failed to create protocol";
      
      if (axios.isAxiosError(error)) {
        // Handle specific error cases
        switch (error.response?.status) {
          case 401:
            errorMessage = "You must be logged in to create a protocol";
            break;
          case 400:
            errorMessage = error.response?.data || "Invalid protocol data";
            break;
          case 404:
            errorMessage = "User not found";
            break;
          case 500:
            errorMessage = "Server error. Please try again later.";
            break;
          default:
            errorMessage = error.response?.data || "Failed to create protocol";
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="2xl"
      className="bg-secondary"
    >
      <ModalContent>
        <ModalHeader className="text-xl">Create New Protocol</ModalHeader>
        <ModalBody className="pb-6">
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  label="Protocol Name"
                  variant="bordered"
                  isInvalid={!!error}
                  errorMessage={error?.message}
                  classNames={{
                    input: "bg-transparent"
                  }}
                />
              )}
            />

            <Controller
              name="frequency"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  label="Frequency"
                  variant="bordered"
                  isInvalid={!!error}
                  errorMessage={error?.message}
                  selectedKeys={field.value ? [field.value] : []}
                  onSelectionChange={(keys) => field.onChange(Array.from(keys)[0])}
                >
                  <SelectItem key="daily" value="daily">Daily</SelectItem>
                  <SelectItem key="weekly" value="weekly">Weekly</SelectItem>
                  <SelectItem key="monthly" value="monthly">Monthly</SelectItem>
                  <SelectItem key="custom" value="custom">Custom</SelectItem>
                </Select>
              )}
            />

            <Controller
              name="description"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <Textarea
                  {...field}
                  label="Description"
                  variant="bordered"
                  isInvalid={!!error}
                  errorMessage={error?.message}
                  classNames={{
                    input: "bg-transparent"
                  }}
                />
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                variant="light"
                onPress={onClose}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                isDisabled={isLoading}
              >
                {isLoading ? "Creating..." : "Create Protocol"}
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};